import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import _ from "lodash";
import { routeSelected, getSessionData, routeHighlighted, customAudit, railInspectionExited } from "../../../redux/actions/index";
import { LazyTippy } from "../../util/LazyTooltips";
import ContentLoader from "react-content-loader";
import { Tooltip } from "antd";
import { calculateBearingText } from "../../util/Geometry";
import { convertToTimezone } from "../../util/TimezoneUtils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSun, faMoon, faArrowRight, faArrowLeft, faCompass, faTags } from "@fortawesome/free-solid-svg-icons";
import Highlighter from "react-highlight-words";
import { MEMOIZED_DOMAIN_URL } from "../../util/HostUtils";
import { resetStillImageAdjustments } from "redux/actions/sessionActions";
import { getVideoFromSessionTimestamp } from "redux/actions/videoFromTimestampActions";
import OBCSpinner from "components/util/OBC";

const userConfigSelector = (state) => state.userDetails.userConfig;
const csrfTokenSelector = (state) => state.csrfToken;
const ICON_DICT = { Day: faSun, Night: faMoon, Forward: faArrowRight, Backward: faArrowLeft, Compass: faCompass };
const GUTTER_SIZE = 8;

const SessionSkeleton = (props) => (
    <ContentLoader
        speed={2}
        width={733.817}
        height={70}
        viewBox="0 0 733.817 70"
        backgroundColor="#5a608a"
        foregroundColor="#4b5175"
        {...props}>
        <rect
            x="5"
            y="5"
            rx="0"
            ry="0"
            width="110"
            height="80"
        />
        <rect
            x="126"
            y="5"
            rx="0"
            ry="0"
            width="206"
            height="12"
        />
        <rect
            x="126"
            y="22"
            rx="0"
            ry="0"
            width="54"
            height="12"
        />
        <rect
            x="126"
            y="39"
            rx="0"
            ry="0"
            width="152"
            height="12"
        />
        <rect
            x="126"
            y="56"
            rx="0"
            ry="0"
            width="303"
            height="12"
        />
    </ContentLoader>
);

export const RouteHistoryTestComp = () => {
    const props = {
        sessionID: 432482,
        point: {
            session_id: 432482,
            name: "T11 SE 15 RAM Paddock Wood to Strood",
            timestamp: 1743500159232.219,
            system: "ELR Mile & Chain",
            route: "PWS1",
            position: "3076.377",
            sub_position: "2100",
            mileage_increasing: true,
            workspace_id: 340,
            session_tags: ["Day", "Thermal FFV", "Visible FFV"],
            stream_info: {
                source: 0,
                info: {
                    label: "Visible FFV",
                    type: "Visible FFV",
                    flags: {},
                },
            },
        },
        currentItem: false,
        videoKey: "s0.c0.f25.t1951e006e68",
        availableTags: ["Day", "Dusk", "Inspection", "NIR FFV", "Thermal FFV", "Track Geometry", "Visible FFV"],
        refProp: null,
        sessionSearch: "",
        tagFilter: "all",
        style: {
            position: "absolute",
            left: 0,
            top: 450,
            height: 90,
            width: "100%",
        },
    };
    return (
        <div className="RouteHistoryTab">
            <div className="RouteHistoryListContainer">
                <div className="HistoryAutoSizer">
                    <RouteHistoryListItem {...props} />
                </div>
            </div>
        </div>
    );
};

const RouteHistoryListItem = ({ sessionID, point, currentItem, videoKey, availableTags, sessionSearch, tagFilter, style }) => {
    const dispatch = useDispatch();
    const [sessionData, setSessionData] = useState({});
    const [sessionIDCopyText, setSessionIDCopyText] = useState("Click to copy");
    const userConfig = useSelector(userConfigSelector);
    const csrfToken = useSelector(csrfTokenSelector);
    const [imageData, setImageData] = useState(null);
    const [loadingImageData, setLoadingImageData] = useState(true);
    const [loadingImage, setLoadingImage] = useState(true);

    useEffect(() => {
        dispatch(getVideoFromSessionTimestamp(sessionID, point.timestamp)).then((videoData) => {
            setLoadingImageData(false);
            setImageData(videoData);
        });
    }, [dispatch, point, sessionID]);

    useEffect(() => {
        dispatch(getSessionData(sessionID)).then((sessionData) => {
            setSessionData(sessionData);
        });
    }, []);

    const bearingText = useMemo(() => {
        if (point.bearing === null) {
            return "Unknown";
        }

        return calculateBearingText(point.bearing);
    }, [point.bearing]);

    const date = useMemo(() => {
        const dpDate = new Date(point.timestamp);
        return convertToTimezone(dpDate, userConfig.convert_to_utc);
    }, [point.timestamp, userConfig.convert_to_utc]);

    const direction = useMemo(() => {
        if (!sessionData) {
            return null;
        }
        let direction = null;
        if (sessionData.tags && sessionData.tags.includes("Forward")) {
            direction = "Forward";
        } else if (sessionData.tags && sessionData.tags.includes("Backward")) {
            direction = "Backward";
        }
        return direction;
    }, [sessionData]);

    const dayOrNight = useMemo(() => {
        return sessionData && sessionData.tags && sessionData.tags.includes("Day") ? "Day" : "Night";
    }, [sessionData]);

    const sessionTags = useMemo(() => {
        let tags = [];
        if (!sessionData) {
            return tags;
        }

        tags = _.filter(sessionData.tags, (tag) => {
            return !["Day", "Night", "Backward", "Forward"].includes(tag) && availableTags.includes(tag);
        }).sort((a, b) => {
            if (a === tagFilter) return -1; // Move the tagFilter item to the beginning
            if (b === tagFilter) return 1; // Move the tagFilter item to the beginning
            return a.localeCompare(b); // Sort the rest alphabetically
        });

        if (tags.length > 0) {
            return (
                <div className="Tag TagList">
                    <FontAwesomeIcon
                        style={{ marginRight: "3px" }}
                        icon={faTags}
                    />
                    <Highlighter
                        highlightClassName="ObcTextHighlight"
                        searchWords={tagFilter !== "all" ? [tagFilter] : []}
                        autoEscape={true}
                        textToHighlight={_.chain(tags).join(", ").value()}
                    />
                </div>
            );
        } else {
            return null;
        }
    }, [sessionData, availableTags, tagFilter]);

    const onMouseOver = () => {
        dispatch(routeHighlighted(sessionID));
    };

    const onMouseOut = () => {
        dispatch(routeHighlighted(null));
    };

    const handleSessionIDClick = (e, id) => {
        e.preventDefault();
        e.stopPropagation();

        if (navigator && navigator.clipboard) {
            navigator.clipboard.writeText(id);
            setSessionIDCopyText("Copied!");
        }
    };

    const selectSource = useCallback(() => {
        const sessionID = point.session_id;

        dispatch(
            customAudit(
                "history_tab_item_selected",
                {
                    sessionID: sessionID,
                    timestamp: point.timestamp,
                },
                `History tab item selected - Session #${sessionID}`,
            ),
        );
        console.log("debug point", point);
        dispatch(resetStillImageAdjustments());
        dispatch(routeSelected(sessionID, point.timestamp / 1000, undefined, undefined, _.get(point, ["stream_info", "source"])));
        dispatch(railInspectionExited());
    }, [dispatch, point]);

    const sourceImageData = useMemo(() => {
        if (loadingImageData) {
            return (
                <div className="HistoryListImage Spinner">
                    <OBCSpinner
                        size={40}
                        speed={3}
                        color={"#e8dfff"}
                    />
                </div>
            );
        }

        const source = _.get(point, ["stream_info", "source"]);
        const sourceImageData = _.get(imageData, [source]);

        if (!sourceImageData) {
            return null;
        }

        let imageURL = `https://raw${MEMOIZED_DOMAIN_URL}/${sourceImageData[0]}/${sourceImageData[1]}/${sourceImageData[2]}.jpg`;

        if (csrfToken) {
            imageURL += `?csrf=${csrfToken}`;
        }
        const imageClasses = ["RouteHistoryListImage"];
        const className = imageClasses.join(" ");
        const largeImage = (
            <img
                src={imageURL}
                crossOrigin={"anonymous"}
                key={source}
                className={className}
                alt=""
            />
        );
        return (
            <LazyTippy
                placement="top"
                arrow={false}
                theme="dark"
                className="HistoryResultImageToolTip"
                content={largeImage}>
                <div className={"HistoryListImage" + (loadingImage ? " Spinner" : "")}>
                    {loadingImage && (
                        <OBCSpinner
                            size={40}
                            speed={3}
                            color={"#e8dfff"}
                        />
                    )}
                    <img
                        src={imageURL}
                        onLoad={() => setLoadingImage(false)}
                        crossOrigin={"anonymous"}
                        key={source}
                        className={className}
                        alt=""
                        style={{
                            display: loadingImage ? "none" : "block",
                        }}
                    />
                </div>
            </LazyTippy>
        );
    }, [csrfToken, imageData, loadingImage, loadingImageData, point]);

    const handleSessionIDHovered = (e) => {
        if (e && sessionIDCopyText === "Copied!") {
            setSessionIDCopyText("Click to copy");
        }
    };

    if (!_.get(sessionData, "route_name")) {
        return (
            <div
                style={{
                    ...style,
                    left: style.left + 4,
                    top: style.top + GUTTER_SIZE,
                    width: "98%",
                    height: style.height - GUTTER_SIZE,
                }}
                className="historyListItem">
                <SessionSkeleton />
            </div>
        );
    }

    return (
        <div
            onMouseEnter={onMouseOver}
            onMouseLeave={onMouseOut}
            key={point.video_key}
            className={"historyListItem" + (currentItem ? " active" : "")}
            onClick={selectSource}
            style={{
                ...style,
                left: style.left + 4,
                top: style.top + GUTTER_SIZE,
                // width: style.width,
                width: "98%",
                height: style.height - GUTTER_SIZE,
            }}>
            {sourceImageData}
            <div className="HistoryItemMainContent">
                <div className="HistoryItemRow HistoryItemRowTitle">
                    <Tooltip
                        title={sessionData?.route_name || "Unknown"}
                        mouseEnterDelay={0.1}
                        mouseLeaveDelay={0}>
                        <Highlighter
                            highlightClassName="ObcTextHighlight"
                            searchWords={sessionSearch.split(" ")}
                            autoEscape={true}
                            textToHighlight={sessionData?.route_name || "Unknown"}
                        />
                    </Tooltip>
                </div>
                <div className="HistoryItemRow">
                    <Tooltip
                        title={sessionIDCopyText}
                        mouseEnterDelay={0.05}
                        mouseLeaveDelay={0}
                        onVisibleChange={(e) => handleSessionIDHovered(e)}>
                        <span
                            className="Small SessionID"
                            onClick={(e) => handleSessionIDClick(e, sessionID)}>
                            #
                            <Highlighter
                                highlightClassName="ObcTextHighlight"
                                searchWords={sessionSearch.split(" ")}
                                autoEscape={true}
                                textToHighlight={String(sessionID)}
                            />
                        </span>
                    </Tooltip>
                </div>
                <div className="HistoryItemRow">
                    <div className="Date">
                        <span className="Small">{date}</span>
                    </div>
                </div>

                <div className="HistoryItemRow">
                    {dayOrNight && (
                        <div className="Tag">
                            <FontAwesomeIcon
                                icon={ICON_DICT[dayOrNight]}
                                style={{ marginRight: "3px", color: dayOrNight === "Day" ? "#F8C46D" : "#728dd4" }}
                            />
                            <span className="Small">{dayOrNight}</span>
                        </div>
                    )}
                    {direction && (
                        <div className="Tag">
                            <FontAwesomeIcon
                                icon={ICON_DICT[direction]}
                                style={{ marginRight: "4px" }}
                            />
                            <span className="Small">{direction}</span>
                        </div>
                    )}
                    {bearingText && (
                        <div className="Tag">
                            <FontAwesomeIcon
                                icon={ICON_DICT["Compass"]}
                                style={{ marginRight: "4px" }}
                            />
                            <span className="Small">{bearingText}</span>
                        </div>
                    )}
                    {point.sub_position && (
                        <Tooltip title="Track ID">
                            <div className="Tag">
                                <span className="Small">{point.sub_position}</span>
                            </div>
                        </Tooltip>
                    )}
                    {sessionTags}
                </div>
            </div>
        </div>
    );
};

export default RouteHistoryListItem;
